import { FC, ReactNode, useState } from 'react';
import { Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';

interface ICreatePostingMethodCardProps {
  icon: ReactNode;
  label: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
  dataCy?: string;
}

/**
 * Selectable card used in the "How do you want to create your posting?" popup.
 * Mirrors the selection/hover affordances of `ProfessionTypeCard`; selection
 * state is owned by the parent.
 */
export const CreatePostingMethodCard: FC<ICreatePostingMethodCardProps> = ({
  icon,
  label,
  description,
  isSelected,
  onClick,
  dataCy,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getBoxShadow = () => {
    if (!isSelected && isHovered) {
      return '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)';
    }
    return 'none';
  };

  return (
    <Column
      data-testid="create-posting-method-card"
      data-cy={dataCy}
      align="center"
      gap={2}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: 200,
        padding: 16,
        borderRadius: 8,
        backgroundColor: isSelected ? colors.primary[50] : colors.neutral[50],
        border: `2px solid ${isSelected ? colors.secondary[500] : colors.neutral[50]}`,
        boxShadow: getBoxShadow(),
        transform: !isSelected && isHovered ? 'scale(1.04)' : 'scale(1)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      }}
    >
      {icon}
      <Typography as="span" bold variant="body1" color={colors.neutral[600]} textAlign="center">
        {label}
      </Typography>
      <Typography as="span" size="body_small" color={colors.neutral[500]} textAlign="center">
        {description}
      </Typography>
    </Column>
  );
};
